/**
 * @file OpenTelemetry Fetch Wrapper
 * @description A wrapper around the Fetch API that integrates OpenTelemetry tracing, logging, and metrics.
 */

import { tracer } from '../../tracers';
import otlFetchStart from './otlFetchStart';
import otlFetchSuccess from './otlFetchSuccess';
import otlFetchReject from './otlFetchReject';
import { buildSpanOptionsOfHttpRequest } from '../../utils';
import { HTTP_CLIENTS } from '../../constants';
import { buildOTSpanName } from '../../utils/buildOTSpanName';
import { traceContextService } from '../../services/TraceContextService';

/**
 * Wraps the Fetch API with OpenTelemetry instrumentation, handling request tracing, logging, and response metrics.
 *
 * @param {string} url - The URL of the HTTP request.
 * @param {Object} options - The fetch options.
 * @param {string} options.method - The HTTP method (e.g., 'GET', 'POST').
 * @param {string} [options.body] - The request body in JSON format.
 * @param {Object} [traceData] - Additional trace-related data for building the span name.
 * @returns {Promise<Response>} A promise that resolves to the response object or rejects with an error.
 */
function fetchWithOtl(...args) {
    const [url, { method, body }, traceData] = args;
    const span = tracer.startSpan(
        buildOTSpanName(traceData, { method, url, data: JSON.parse(body) }),
        buildSpanOptionsOfHttpRequest(url, method, HTTP_CLIENTS.FETCH),
        traceContextService.getActiveParentContext(),
    );
    otlFetchStart(span, ...args);

    return fetch(...args)
        .then(async (response) => {
            const responseData = await response.json();
            response.responseData = responseData;

            // Handle response errors, including cases where response is 200 but contains an error status
            if (!response.ok || responseData.status === 0) {
                return Promise.reject(response);
            }

            return response;
        })
        .then((response) => {
            otlFetchSuccess(span, url, method, response);

            return Promise.resolve(response);
        })
        .catch((error) => {
            otlFetchReject(span, url, method, error);

            return Promise.reject(error);
        });
}

export default fetchWithOtl;
